import React, { useEffect, useState } from "react";
import { Crown, Sparkles, ShieldCheck, Feather, ArrowUpRight, ChevronRight } from "lucide-react";
import { adminApi, publicApi } from "../lib/api";

/**
 * Small plan indicator displayed at the top of the chat panel.
 * - Admin JWT detected → "Admin illimité" (no CTA)
 * - Pro subscription     → "Pro"            (no CTA)
 * - Standard subscription→ "Standard"       (CTA: upgrade Pro)
 * - None / anonymous     → "Découverte"     (CTA: subscribe)
 *
 * The onUpgrade callback opens the SubscribeModal. When plan is null (loading),
 * renders a compact skeleton so the layout does not jump.
 */
export default function PlanBadge({ onUpgrade }) {
  const [state, setState] = useState({ plan: null, isAdmin: false, remaining: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // 1) Try admin first — if lexhab_token present, hit /admin/me
      const adminToken = localStorage.getItem("lexhab_token");
      if (adminToken) {
        try {
          const { data } = await adminApi.me();
          if (!cancelled && data?.email) {
            setState({ plan: "admin", isAdmin: true, remaining: null, role: data.role });
            setLoading(false);
            return;
          }
        } catch (_) {
          // fall through to public quota
        }
      }
      // 2) Otherwise rely on public /quota (uses lexhab_sub_token automatically)
      try {
        const { data } = await publicApi.quota();
        if (!cancelled) {
          if (data.is_subscribed) {
            setState({ plan: data.plan || "standard", isAdmin: false, remaining: null });
          } else {
            setState({
              plan: "free",
              isAdmin: false,
              remaining: Math.max(0, (data.lifetime_limit || 2) - (data.lifetime_used || 0)),
              limit: data.lifetime_limit || 2,
              lifetimeUsed: data.lifetime_used || 0,
              lifetimeLimit: data.lifetime_limit || 2,
            });
          }
        }
      } catch (_) {
        if (!cancelled) setState({ plan: "free", isAdmin: false, remaining: null });
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="inline-flex items-center gap-2 bg-white border border-primary/15 rounded-sm px-3 py-1.5 opacity-50" data-testid="plan-badge-skeleton">
        <span className="w-3 h-3 bg-primary/20 rounded-sm" />
        <span className="font-mono text-[10px] uppercase tracking-wider text-foreground/40">
          Chargement…
        </span>
      </div>
    );
  }

  const meta = getMeta(state);
  const showUpgrade = state.plan === "free" || state.plan === "standard";

  return (
    <div
      className={`inline-flex items-center gap-2.5 border rounded-sm px-3 py-1.5 ${meta.wrapper}`}
      data-testid={`plan-badge-${state.plan}`}
    >
      <meta.Icon size={13} strokeWidth={1.9} className={meta.iconClass} />
      <span className={`font-mono text-[10px] uppercase tracking-wider font-semibold ${meta.labelClass}`}>
        {meta.label}
      </span>
      {state.plan === "free" && state.remaining !== null && (
        <span className="font-mono text-[10px] text-foreground/50">
          · {state.remaining}/{state.limit} à vie
        </span>
      )}
      {showUpgrade && (
        <button
          type="button"
          onClick={onUpgrade}
          className="group ml-1 inline-flex items-center gap-1 text-[11px] font-medium text-accent-dark hover:text-[#0D1B2A] transition-colors"
          data-testid="plan-badge-upgrade"
        >
          <ArrowUpRight size={11} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
          Passer Pro
        </button>
      )}
    </div>
  );
}

/**
 * CTA displayed under a Free/Standard AI answer to nudge the user to the Pro mode.
 */
export function UpgradeAnswerCTA({ plan, onUpgrade }) {
  if (plan === "pro" || plan === "admin") return null;
  return (
    <div
      className="mt-3 bg-gradient-to-r from-accent/10 via-white to-accent/10 border border-accent/30 rounded-sm p-4"
      data-testid="upgrade-cta-card"
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 shrink-0 bg-accent text-[#0D1B2A] rounded-sm flex items-center justify-center mt-0.5">
          <Crown size={14} strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-sm font-semibold text-primary leading-tight">
            Voir ce que donnerait une réponse Pro
          </div>
          <p className="text-xs text-foreground/70 mt-1.5 leading-relaxed">
            Le mode Pro active une <strong>analyse approfondie</strong> sur jusqu'à <strong>10 extraits croisés</strong>,
            structure la réponse en 4 parties (cadre légal → procédure → exceptions → conclusion opérationnelle), et
            identifie les articulations entre loi, décret et arrêté — idéal pour les dossiers complexes.
          </p>
        </div>
        <button
          type="button"
          onClick={onUpgrade}
          className="shrink-0 inline-flex items-center gap-1 bg-accent text-[#0D1B2A] px-3 py-2 rounded-sm text-xs font-semibold hover:bg-accent-dark transition-colors"
          data-testid="upgrade-cta-button"
        >
          Passer Pro <ChevronRight size={12} />
        </button>
      </div>
    </div>
  );
}

/* ---------- presentation helpers ---------- */

function getMeta(state) {
  if (state.isAdmin) {
    return {
      Icon: ShieldCheck,
      label: "Admin · illimité",
      wrapper: "bg-[#0D1B2A] border-accent text-white",
      iconClass: "text-accent",
      labelClass: "text-white",
    };
  }
  if (state.plan === "pro") {
    return {
      Icon: Crown,
      label: "Mode Pro · illimité",
      wrapper: "bg-accent/15 border-accent text-[#0D1B2A]",
      iconClass: "text-accent-dark",
      labelClass: "text-[#0D1B2A]",
    };
  }
  if (state.plan === "standard") {
    return {
      Icon: Sparkles,
      label: "Standard · illimité",
      wrapper: "bg-primary/10 border-primary/30 text-primary",
      iconClass: "text-primary",
      labelClass: "text-primary",
    };
  }
  // free / unknown
  return {
    Icon: Feather,
    label: "Découverte",
    wrapper: "bg-white border-primary/20",
    iconClass: "text-foreground/50",
    labelClass: "text-foreground/70",
  };
}
